import { useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import { PanelHeader } from '~/components/panel-header'
import { DialogLink } from '~/components/dialog-link'
import { SearchIcon } from '~/components/icons'
import { Skeleton, SkeletonBlock } from '~/components/skeleton'
import { FilterChip } from '~/components/ui/filter-chip'
import { SidewaysRow } from '~/components/ui/sideways-row'
import { useDataService, useDataState } from '~/data/data-context'
import type { ExploreQuery, LibraryListing } from '~/data/templates'
import { billedCast } from '~/lib/billed-cast'
import { DIALOG_PATHS } from '~/lib/dialogs'
import { ranLabel } from '~/lib/listings'

const SCHEDULES = ['free', 'sequential', 'strict'] as const

/** The counts a template can be narrowed to. Beyond this, searching is the way through. */
const WORKOUT_COUNTS = [2, 3, 4, 5, 6] as const

/** What a row's cast line fits at its type size — see `lib/billed-cast.ts`. */
const ROW_CAST = 52

/** And what a card's fits, a card being narrower than the panel a row runs across. */
const CARD_CAST = 24

/**
 * Explore: the templates other people have published. Everything here comes from the server —
 * a listing is nobody's device's to hold — so this is the one page in the app that says it
 * needs a connection rather than working from what is cached.
 *
 * It is a page of the templates page, the way a settings section is a page of settings: the
 * library you keep and the shelf everybody else writes to, one behind the other — so it stands
 * in the same card, and the way back is the way back to the library.
 */
export function ExplorePanel({ query }: { query: ExploreQuery }) {
  const service = useDataService()
  const navigate = useNavigate()
  const { sets } = useDataState()

  /*
   * Explore opens on three groups and puts them away the moment anything is asked of it —
   * searching or narrowing lists what matches instead, and clearing the search and every
   * filter brings them back. An order asked for is one of those: the groups have orders of
   * their own, so asking for one is asking for the list.
   */
  const asked =
    (query.search ?? '') !== '' ||
    query.schedule !== undefined ||
    query.workouts !== undefined ||
    (query.exercise ?? '') !== '' ||
    query.sort !== undefined

  /*
   * The exercise trained last, off the account's own history. Signed out there is none, and
   * the group that names it simply does not stand — as it does not for anybody who has never
   * trained, or where nothing published names what they trained.
   */
  const trainedLast =
    [...sets].sort((a, b) => b.performedAt - a.performedAt)[0]?.exerciseName ?? null

  /*
   * The answer carries the query it answers, so a query still in flight shows the skeleton
   * without anything having to be cleared on the way in — what is on screen is always what the
   * address asked for, or nothing yet.
   */
  const [answer, setAnswer] = useState<{
    key: string
    listings: LibraryListing[]
    offline: boolean
  } | null>(null)
  const [term, setTerm] = useState(query.search ?? '')
  const [exercise, setExercise] = useState(query.exercise ?? '')
  const narrowing = useRef<ReturnType<typeof setTimeout> | null>(null)

  const key = JSON.stringify(query)
  const held = answer?.key === key ? answer : null
  const listings = held?.listings ?? null
  const offline = held?.offline ?? false

  // The field types freely and the URL catches up, so a keystroke is not a page of results.
  const settling = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (settling.current !== null) {
        clearTimeout(settling.current)
      }
      if (narrowing.current !== null) {
        clearTimeout(narrowing.current)
      }
    },
    [],
  )

  useEffect(() => {
    let alive = true
    service
      .explore(query)
      .then((found) => {
        if (alive) {
          setAnswer({ key, listings: found, offline: false })
        }
      })
      .catch(() => {
        if (alive) {
          setAnswer({ key, listings: [], offline: true })
        }
      })
    return () => {
      alive = false
    }
    // The query is what it depends on; `key` is that query, written so it compares by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, key])

  /*
   * The two groups that are not simply everything published. They are asked for only while the
   * groups stand, so a search costs two requests fewer than it would have.
   */
  const [most, setMost] = useState<LibraryListing[] | null>(null)
  const [named, setNamed] = useState<LibraryListing[] | null>(null)
  useEffect(() => {
    if (asked) {
      return
    }
    let alive = true
    void service
      .explore({ sort: 'popular' })
      .then((found) => {
        if (alive) {
          // Only the ones somebody has actually run: a group of templates nobody has run,
          // headed "what most people run", says something false about all of them.
          setMost(found.filter((each) => each.ranCount > 0))
        }
      })
      .catch(() => undefined)
    return () => {
      alive = false
    }
  }, [service, asked])
  useEffect(() => {
    // Nothing to clear on the way out: the group is drawn only while both hold, so a stale
    // answer is never on screen to be cleared.
    if (asked || trainedLast === null) {
      return
    }
    let alive = true
    void service
      .explore({ exercise: trainedLast })
      .then((found) => {
        if (alive) {
          setNamed(found)
        }
      })
      .catch(() => undefined)
    return () => {
      alive = false
    }
  }, [service, asked, trainedLast])

  function narrow(next: Partial<ExploreQuery>): void {
    void navigate({
      to: '.',
      search: (held) => ({ ...held, explore: { ...query, ...next } }),
      mask: { to: DIALOG_PATHS.explore },
      replace: true,
      resetScroll: false,
      viewTransition: false,
    })
  }

  function toLibrary(): void {
    void navigate({
      to: '.',
      search: ({ explore: _shown, ...held }) => ({ ...held, dialog: 'templates' as const }),
      mask: { to: DIALOG_PATHS.templates },
      resetScroll: false,
      viewTransition: false,
    })
  }

  return (
    <>
      <PanelHeader title="Explore" onBack={toLibrary} backLabel="Back to your library" />
      <div className="w-104 px-4 pb-4">
        <div className="flex items-center gap-2 rounded-full bg-surface-raised px-3">
          <SearchIcon aria-hidden className="size-5 shrink-0 text-ink-muted" />
          <input
            type="search"
            value={term}
            aria-label="Search Explore"
            placeholder="Templates, people, exercises"
            className="h-11 flex-1 bg-transparent text-base outline-none"
            onChange={(event) => {
              const next = event.target.value
              setTerm(next)
              if (settling.current !== null) {
                clearTimeout(settling.current)
              }
              settling.current = setTimeout(() => {
                narrow({ search: next.length > 0 ? next : undefined })
              }, 200)
            }}
          />
        </div>

        {/*
          Narrowing to templates that have you doing one lift in particular, which the free-text
          search cannot do: it matches a template naming the exercise anywhere, while this asks
          for that exercise by name, folded the way every exercise is matched.
        */}
        <input
          type="text"
          value={exercise}
          aria-label="Filter by exercise"
          placeholder="Has this exercise"
          className="mt-2 h-10 w-full rounded-full bg-surface-raised px-4 text-base outline-none"
          onChange={(event) => {
            const next = event.target.value
            setExercise(next)
            if (narrowing.current !== null) {
              clearTimeout(narrowing.current)
            }
            narrowing.current = setTimeout(() => {
              narrow({ exercise: next.length > 0 ? next : undefined })
            }, 200)
          }}
        />

        {/*
          One line, always: a row of filters that wrapped would change height as chips come and
          go, and everything under it would move while somebody was reading it.
        */}
        <div className="-mx-4 mt-3 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <FilterChip
            className="capitalize"
            label="Most run"
            on={query.sort === 'popular'}
            onToggle={() => {
              narrow({ sort: query.sort === 'popular' ? undefined : 'popular' })
            }}
          />
          {SCHEDULES.map((schedule) => (
            <FilterChip
              className="capitalize"
              key={schedule}
              label={schedule}
              on={query.schedule === schedule}
              onToggle={() => {
                narrow({ schedule: query.schedule === schedule ? undefined : schedule })
              }}
            />
          ))}
          {WORKOUT_COUNTS.map((count) => (
            <FilterChip
              className="capitalize"
              key={count}
              label={`${String(count)} workouts`}
              on={query.workouts === count}
              onToggle={() => {
                narrow({ workouts: query.workouts === count ? undefined : count })
              }}
            />
          ))}
        </div>

        <div className="pt-4">
          {offline ? (
            <p className="text-sm text-ink-muted">Explore needs a connection.</p>
          ) : listings === null ? (
            <SkeletonBlock label="Loading Explore" className="space-y-2">
              <Skeleton className="h-20 rounded-2xl" />
              <Skeleton className="h-20 rounded-2xl" />
            </SkeletonBlock>
          ) : listings.length === 0 ? (
            <p className="text-sm text-ink-muted">
              {query.search !== undefined && query.search.length > 0
                ? `Nothing published matches ${query.search}.`
                : 'Nothing has been published yet.'}
            </p>
          ) : (
            <>
              {/*
                The groups Explore opens on, above everything published. Each of the first two
                is a rail of cards rather than a run of rows: they are a handful of suggestions
                read across, where the list below is the whole shelf read down.
              */}
              {!asked && most !== null && most.length > 0 && (
                <Group label="What most people run" query={query} listings={most} />
              )}
              {!asked && trainedLast !== null && named !== null && named.length > 0 && (
                <Group label={`Templates with ${trainedLast}`} query={query} listings={named} />
              )}
              {!asked && (
                <h3 className="pt-2 pb-1 text-xs font-medium tracking-wider text-ink-muted uppercase">
                  Everything published
                </h3>
              )}
              <ul aria-label="Published templates" className="space-y-1">
                {listings.map((listing) => (
                  <li
                    key={listing.templateId}
                    className="relative rounded-2xl p-3 transition-colors hover:bg-hover"
                  >
                    <DialogLink
                      dialog="template"
                      templateId={listing.templateId}
                      // What Explore is showing goes with it, so the page's way back comes back
                      // here rather than to the library — narrowed or not.
                      explore={query}
                      className="block truncate font-medium after:absolute after:inset-0 after:rounded-2xl"
                    >
                      {listing.name}
                    </DialogLink>
                    <p className="truncate text-sm text-ink-muted">
                      {listing.authorName ?? 'Somebody who has left'} · {listing.schedule} ·{' '}
                      {String(listing.workoutCount)}{' '}
                      {listing.workoutCount === 1 ? 'workout' : 'workouts'}
                    </p>
                    {/*
                    The exercises it names, in the order the script names them. What a template
                    is, before its author or its schedule: somebody browsing is looking for the
                    lifts they want to do. Cut to one line the way a poster's billing line is,
                    by the same hand — a script naming a dozen shows what fits and says there
                    are more.
                  */}
                    {listing.exercises.length > 0 && (
                      <p className="truncate pt-1 text-sm text-ink-muted">
                        {billedCast(listing.exercises, ROW_CAST)}
                      </p>
                    )}
                    {listing.description !== null ? (
                      <p className="line-clamp-2 pt-1 text-sm text-ink-muted/80">
                        {listing.description}
                      </p>
                    ) : null}
                    <p className="pt-1 text-sm text-ink-muted">{ranLabel(listing.ranCount)}</p>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </>
  )
}

/**
 * One of the groups Explore opens on: a heading of its own over a rail of cards, each opening
 * the template's page the way a row does. A card rather than a row because a group is a handful
 * of suggestions, and a handful read across the page does not push the shelf below it off the
 * bottom of the panel.
 */
function Group({
  label,
  query,
  listings,
}: {
  label: string
  /** What Explore is showing, so a template's way back comes back here. */
  query: ExploreQuery
  listings: readonly LibraryListing[]
}) {
  return (
    <section aria-label={label} className="pb-3">
      <h3 className="pb-1 text-xs font-medium tracking-wider text-ink-muted uppercase">{label}</h3>
      <SidewaysRow as="ul" label={label} className="gap-2">
        {listings.map((listing) => (
          <li
            key={listing.templateId}
            className="relative w-56 shrink-0 rounded-2xl bg-surface-raised p-3 ring-1 ring-white/10 transition-colors hover:bg-hover"
          >
            <DialogLink
              dialog="template"
              templateId={listing.templateId}
              explore={query}
              className="block truncate font-medium after:absolute after:inset-0 after:rounded-2xl"
            >
              {listing.name}
            </DialogLink>
            <p className="truncate text-sm text-ink-muted">
              {listing.authorName ?? 'Somebody who has left'} · {String(listing.workoutCount)}{' '}
              {listing.workoutCount === 1 ? 'workout' : 'workouts'}
            </p>
            {listing.exercises.length > 0 && (
              <p className="truncate pt-1 text-sm text-ink-muted">
                {billedCast(listing.exercises, CARD_CAST)}
              </p>
            )}
            <p className="truncate pt-1 text-sm text-ink-muted">{ranLabel(listing.ranCount)}</p>
          </li>
        ))}
      </SidewaysRow>
    </section>
  )
}
